const { productLib } = require("@nuskin/product-lib");
/**
 *
 * @param {String} slug - if present, productLib.getProduct will get product data via slug
 * @param {String} id - if present, productLib.getProduct will get product data via id, uid, or sku (whichever the id is)
 * @param {String} env - get product data from this environment
 * @param {String} market - get product data from this market
 * @param {String} language - get product data from this language
 * @param {String} qty - for getPricing(), get product pricing using this quantity
 * @param {String} skuIfNotFirstVariant - for getPricing(), if the sku isn't the first variant sku, use this sku to assign sku and id for getting pricing
 * @param {String} oktaToken - if given will change the way pricing and points are received.
 * @param {Array} selectedSkus - selected skus for a bundle if you want to get skus from required products
 * @returns {Object} - Product Data Object e.g. { data: { id: "12345678", etc: "etc"}}
 */
export async function getProduct(
  slug,
  id,
  env,
  market,
  language,
  qty,
  skuIfNotFirstVariant,
  oktaToken,
  selectedSkus
) {
  if (slug) {
    let productFromSlug = await productLib.getProduct({
      fromSlug: slug,
      env,
      market,
      language
    });
    await productFromSlug.getPricing(
      qty,
      oktaToken,
      skuIfNotFirstVariant,
      selectedSkus
    );
    return productFromSlug;
  } else {
    let productFromIdUidOrSku = await productLib.getProduct({
      fromId: id,
      env,
      market,
      language
    });
    await productFromIdUidOrSku.getPricing(
      qty,
      oktaToken,
      skuIfNotFirstVariant,
      selectedSkus
    );
    return productFromIdUidOrSku;
  }
}

/**
 *
 * @param {String} market - market to grab product detail strings from within Contentstack
 * @param {String} language - language to grab product detail strings from within Contentstack
 * @param {String} env - environment to grab product detail strings from within Contentstack
 * @returns {Object} - Translations object e.g. { addToCartButton: "Add to Cart", subscribeButton: "Subscribe Monthly", etc: "etc"}
 */
export async function getTranslations(market, language, env) {
  const translations = await productLib.getPDPStrings(market, language, env);
  return translations;
}
/**
 *
 * @param {String} market - market to grab category strings from within Contentstack
 * @param {String} language - language to grab category strings from within Contentstack
 * @param {String} env - environment to grab category strings from within Contentstack
 * @returns {Object} - Translations object e.g. { addToCartButton: "Add to Cart", subscribeButton: "Subscribe Monthly", etc: "etc"}
 */
export async function getCategoryTranslations(market, language, env) {
  const translations = await productLib.getCPStrings(market, language, env);
  return translations;
}
/**
 *
 * @param {Array} ids - array of strings where each string is a product ID
 * @param {String} market get product data from this market
 * @param {String} language get product data in this language
 * @param {String} useContentSource - get product data from this source; 'CS' for contentstack, 'Equinox' for equinox
 * @param {Int} quantity - quantity of products you want to retrieve
 * @param {String} okta - if given will change the way pricing and points are received.
 * @param {String} env - get product data from this environment
 * @returns {Object} - with products key containing array of product data e.g. { products: [{id: '123'}, {id: '124'}]}
 */
export async function getProducts(
  ids,
  market,
  language,
  useContentSource,
  quantity,
  okta,
  env
) {
  if (ids && ids.length > 0) {
    const products = await productLib.getProductsById(
      ids,
      market,
      language,
      useContentSource,
      quantity,
      okta,
      env
    );
    return products;
  }
  return null;
}
